import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MerchantStoreIds {
  userId: string;
  /** stores.id owned by the user (flash_sales.store_id references stores). */
  storeIds: string[];
  /** Every id products.store_id may hold for this merchant — same mapping as the
   *  set_product_store_id() trigger: stores.owner_id, store_settings.user_id, active staff. */
  productStoreIds: string[];
}

export function useMerchantStoreIds() {
  return useQuery({
    queryKey: ["merchant-store-ids"],
    queryFn: async (): Promise<MerchantStoreIds | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const [stores, settings, staff] = await Promise.all([
        supabase.from("stores").select("id").eq("owner_id", user.id),
        supabase.from("store_settings").select("id").eq("user_id", user.id),
        supabase.from("staff_members").select("store_id").eq("user_id", user.id).eq("status", "active"),
      ]);
      const candidates = Array.from(new Set([
        ...(stores.data ?? []).map((r) => r.id),
        ...(settings.data ?? []).map((r) => r.id),
        ...(staff.data ?? []).map((r) => r.store_id).filter(Boolean) as string[],
      ]));
      // Server-side resolution (owns_store_id): an id shared by stores and store_settings
      // with different owners is granted only to the store_settings owner — never via the other row.
      const checks = await Promise.all(candidates.map(async (id) => {
        const { data } = await (supabase.rpc as any)("owns_store_id", { _store_id: id });
        return data === true ? id : null;
      }));
      const productStoreIds = checks.filter(Boolean) as string[];
      const owned = new Set(productStoreIds);
      const storeIds = (stores.data ?? []).map((r) => r.id).filter((id) => owned.has(id));
      return { userId: user.id, storeIds, productStoreIds };
    },
  });
}
